import type { ReactNode } from 'react';
import { Inbox, AlertTriangle } from 'lucide-react';
import { ApiError } from '../../lib/api-client';
import { Button } from './button';
import { SkeletonRows } from './skeleton';

export function EmptyState({ icon, title, description, action }: { icon?: ReactNode; title: string; description?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border py-16 text-center">
      <div className="text-ink-faint">{icon ?? <Inbox size={32} />}</div>
      <div>
        <p className="text-sm font-medium text-ink">{title}</p>
        {description ? <p className="mt-1 max-w-sm text-sm text-ink-muted">{description}</p> : null}
      </div>
      {action}
    </div>
  );
}

/** A calm, plain-language explanation of an API error — never a raw status code or stack. */
export function friendlyErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.detail === 'ONLY_OWNER_CAN_ASSIGN_OR_REMOVE_OWNER') {
      return 'Only a workspace Owner can assign or remove the Owner role.';
    }
    switch (error.code) {
      case 'FORBIDDEN':
        return "You don't have permission to do that. Ask a workspace admin if you think this is a mistake.";
      case 'NOT_FOUND':
        return "We couldn't find that — it may have been removed, or you may not have access to it.";
      case 'INVALID_BODY':
        return 'Some of the information provided was invalid. Please check the form and try again.';
      case 'CONFLICT':
        return 'That already exists or conflicts with something else.';
      case 'HTTP_401':
        return 'Your session has expired. Please sign in again.';
      case 'OUT_OF_SCOPE':
        return 'That target is outside the approved scope for this engagement, so it was blocked.';
      case 'KALI_INTERNAL_ZONE_UNREACHABLE':
        return 'No internal agent connected. Install the DefendIQ agent on a machine inside your network first.';
      case 'OWNERSHIP_PROOF_FAILED':
        return "We couldn't verify ownership with the information provided. Double-check the token and try again.";
      case 'INVALID_TRANSITION':
        return "That action isn't possible from the current state.";
      case 'CANNOT_DEMOTE_LAST_OWNER':
        return 'Every workspace needs at least one Owner — promote another Owner first if you want to change this.';
      case 'CANNOT_REMOVE_LAST_OWNER':
        return "This is the workspace's last Owner, so they can't be removed. Add another Owner first.";
      case 'CANNOT_DISABLE_LAST_OWNER':
        return "This is the workspace's last Owner, so their access can't be paused. Add another Owner first.";
      case 'INVALID_CURRENT_PASSWORD':
        return "That's not your current password. Please try again.";
      case 'AUTHORIZATION_REQUIRED':
        return 'Exploitation proofs require an active authorization record for this target.';
      case 'NOT_INTERNAL':
        return 'Active exploitation is only available for internal targets via the connected agent. External findings use AI-based exploitation proofs.';
      case 'AGENT_NOT_AVAILABLE':
        return 'The connected agent for this finding is no longer available.';
      case 'METASPLOIT_NOT_AVAILABLE':
        return 'Metasploit not available on the connected agent. Install Metasploit Framework on the agent host to enable.';
      case 'AGENT_MISMATCH':
        return "That agent no longer matches this finding — refresh the module preview and try again.";
      case 'CONFIRMATION_PHRASE_MISMATCH':
        return "The typed confirmation phrase didn't match exactly. Please retype it.";
      case 'NO_MODULE_AVAILABLE':
        return 'Could not determine a safe Metasploit auxiliary module for this finding.';
      case 'USER_SESSION_REQUIRED':
        return 'Active exploitation must be authorized by a logged-in user, not an API key.';
      default:
        return 'Something went wrong on our end. Please try again in a moment.';
    }
  }
  return 'Something went wrong. Please try again.';
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border py-16 text-center">
      <AlertTriangle size={28} className="text-warning" />
      <p className="max-w-sm text-sm text-ink-muted">{friendlyErrorMessage(error)}</p>
      {onRetry ? (
        <Button variant="outline" size="sm" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}

export function LoadingState() {
  return <SkeletonRows rows={4} />;
}
